import { queryOptions } from "@tanstack/react-query";
import type { Server, ServerResponse } from "@/app/servers/types";
import type { ScopedBinding } from "@/app/types";
import type { ApiClient } from "@/lib/api";
import { getEngineApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import { serversKeys } from "./query-keys";

// The engine pages this endpoint and applies its own default when no limit is
// asked for, so a single request silently returns a prefix of the project's
// servers. Callers treat the result as the whole set — the catalog decides
// from it whether a server is already registered — so read until the reported
// total is in hand.
//
// The page is deliberately far larger than any project is expected to need.
// The endpoint builds a response for every server before it slices the page, so
// a second request repeats that whole pass rather than costing a slice of it;
// one big page is cheaper than ten small ones, and a project that somehow
// exceeds it still reads correctly, just at that price.
const PAGE_SIZE = 1000;
// A safety bound, not a silent cut-off: exceeding it must fail the query rather
// than make a server beyond the last page appear absent from the project.
const MAX_PAGES = 1000;

/** Walks an offset-paged endpoint to the end of its list. */
export const collectPages = async <T>({
  fetchPage,
  pageSize = PAGE_SIZE,
  maxPages = MAX_PAGES,
}: {
  fetchPage: (options: {
    offset: number;
    limit: number;
  }) => Promise<{ items: T[]; total?: number }>;
  pageSize?: number;
  maxPages?: number;
}): Promise<T[]> => {
  const collected: T[] = [];

  for (let page = 0; page < maxPages; page++) {
    // Resume from what is actually in hand. A page that comes back shorter than
    // it was asked for would otherwise leave a gap the next offset steps over.
    const { items, total } = await fetchPage({
      offset: collected.length,
      limit: pageSize,
    });
    collected.push(...items);

    // Nothing came back, so nothing will; without this the loop spends the rest
    // of its budget asking again.
    if (items.length === 0) {
      if (typeof total === "number" && collected.length < total) {
        throw new Error("Server list ended before its reported total");
      }
      return collected;
    }

    // The reported total decides when the endpoint gives one, because a short
    // page does not mean the last page: anything that caps a page below what
    // was asked for makes a truncated read look complete. Page length is the
    // fallback for an endpoint that reports no total.
    const done =
      typeof total === "number"
        ? collected.length >= total
        : items.length < pageSize;
    if (done) {
      return collected;
    }
  }

  throw new Error(`Server list exceeded ${maxPages} pages before completion`);
};

const fetchServers = ({ client }: { client: ApiClient }): Promise<Server[]> =>
  collectPages<Server>({
    fetchPage: async ({ offset, limit }) => {
      const { data } = await client.get<ServerResponse>("/workers", {
        params: { limit, offset },
      });
      return { items: data?.items ?? [], total: data?.total_count };
    },
  });

export const serversQueryOptions = ({
  binding = "project",
  scope,
}: {
  binding?: ScopedBinding;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<Server[]>({
    queryKey: serversKeys.list({ binding, scope }),
    queryFn: () => fetchServers({ client }),
  });
};
